/**
 * Day 82 P6.1 — provenance source vocabulary.
 *
 * Centralized constants for the `source` key inside per-field provenance
 * envelopes ({source, reason, set_at, set_by, previous_value}). App-side
 * code supplies source + reason at write-time per L5 defensive-vs-authoritative
 * split; the per-table provenance_<table>_capture triggers enrich the
 * envelope with set_at + set_by (and previous_value on UPDATE).
 *
 * Adding a new source: append a constant + export it via PROVENANCE_SOURCES.
 * Phase 8.3 CANONICAL_PATTERNS evaluation will assess promotion to a shared
 * pattern across future provenance-capture surfaces.
 */

/** @public — keep-by-design (U11 census C1): Conv-51 / Decision K retention — historical rows, no backfill (the C5 note below + useImport.ts carry the ruling). */
export const PROVENANCE_SOURCE_ALGOPIX_IMPORT = 'algopix_import' as const;
// C5 (Phase Confluence) — the source-neutral go-forward name for sheet-import writes (Conv-51 / Decision K
// organic shed). New cost + algopix_data + map_price provenance from the import path carries this;
// PROVENANCE_SOURCE_ALGOPIX_IMPORT is RETAINED for historical rows (no backfill — accurate history).
export const PROVENANCE_SOURCE_SHEET_IMPORT = 'sheet_import' as const;
export const PROVENANCE_SOURCE_ALGOPIX_DISCOVERY = 'algopix_discovery' as const;
export const PROVENANCE_SOURCE_DIRECTORY_CREATE = 'directory_create' as const;
export const PROVENANCE_SOURCE_DIRECTORY_EDIT = 'directory_edit' as const;
export const PROVENANCE_SOURCE_MANUAL = 'manual' as const;
// W4a (Phase Wellspring) — the in-house identifier→ASIN resolver. Provider-neutral: covers BOTH
// the clean-UPC deterministic write AND the fuzzy-candidate promote, and distinguishes resolver-
// origin asins from the legacy algopix_import cohort (the verification-gate distinction).
const PROVENANCE_SOURCE_IN_HOUSE_RESOLVER = 'in_house_resolver' as const;

export type ProvenanceSource =
  | typeof PROVENANCE_SOURCE_ALGOPIX_IMPORT
  | typeof PROVENANCE_SOURCE_SHEET_IMPORT
  | typeof PROVENANCE_SOURCE_ALGOPIX_DISCOVERY
  | typeof PROVENANCE_SOURCE_DIRECTORY_CREATE
  | typeof PROVENANCE_SOURCE_DIRECTORY_EDIT
  | typeof PROVENANCE_SOURCE_MANUAL
  | typeof PROVENANCE_SOURCE_IN_HOUSE_RESOLVER;

const PROVENANCE_SOURCES = {
  ALGOPIX_IMPORT: PROVENANCE_SOURCE_ALGOPIX_IMPORT,
  SHEET_IMPORT: PROVENANCE_SOURCE_SHEET_IMPORT,
  ALGOPIX_DISCOVERY: PROVENANCE_SOURCE_ALGOPIX_DISCOVERY,
  DIRECTORY_CREATE: PROVENANCE_SOURCE_DIRECTORY_CREATE,
  DIRECTORY_EDIT: PROVENANCE_SOURCE_DIRECTORY_EDIT,
  MANUAL: PROVENANCE_SOURCE_MANUAL,
  IN_HOUSE_RESOLVER: PROVENANCE_SOURCE_IN_HOUSE_RESOLVER,
} as const;
